import React from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { brand, clearAccessToken, getAccessToken } from "./brand";
import {
  isAccessExpiredResponse,
  isTrialRestrictedResponse,
  notifyAccessExpired,
  redirectToAccessExpiredPage,
} from "./lib/accessExpired";
import { openUpgradeModal } from "./lib/upgradeModal";

const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL, // TODO: use env var based on environment
  withCredentials: true, // Always send cookies or credentials in requests
});

// Add a request interceptor to always use the latest token
axiosClient.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization; // remove header if no token
    }
    return config;
  },
  (error) => Promise.reject(error),
);

function getRateLimitMessage(error: any): string {
  const data = error?.response?.data;
  const retryAfterSeconds = Number(data?.retryAfterSeconds);
  const baseMessage =
    typeof data?.error === "string" && data.error.trim().length > 0
      ? data.error
      : "Too many requests. Please slow down and try again shortly.";

  if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
    const minutes = Math.ceil(retryAfterSeconds / 60);
    if (minutes >= 2) {
      return `${baseMessage} Try again in about ${minutes} minutes.`;
    }
    return `${baseMessage} Try again in about ${retryAfterSeconds} seconds.`;
  }

  return baseMessage;
}

// Add a response interceptor
axiosClient.interceptors.response.use(
  // Handle successful responses
  (response) => response,
  // Handle errors
  async (error) => {
    if (error.response?.status === 401) {
      // Avoid redirect loop if already on login page
      const siteUrl = process.env.REACT_APP_SITE_URL || brand.appUrl;
      const loginUrl = `${siteUrl}/login`;
      if (window.location.href !== loginUrl) {
        toast.error("Your session has expired. Please log in again.", {
          id: "session-expired",
          duration: 4000,
        });
        clearAccessToken();
        window.setTimeout(() => {
          window.location.href = loginUrl;
        }, 1200);
      }
    }

    if (isAccessExpiredResponse(error)) {
      notifyAccessExpired();
      redirectToAccessExpiredPage();
    }

    if (isTrialRestrictedResponse(error)) {
      toast(
        () =>
          React.createElement(
            "span",
            null,
            "This feature isn't included in the free trial. ",
            React.createElement(
              "button",
              {
                type: "button",
                onClick: () => {
                  toast.dismiss("trial-restricted");
                  openUpgradeModal();
                },
                style: {
                  border: "none",
                  background: "none",
                  padding: 0,
                  color: "var(--brand-primary)",
                  fontWeight: 600,
                  textDecoration: "underline",
                },
              },
              "Upgrade for full access",
            ),
          ),
        { id: "trial-restricted", duration: 6000 },
      );
    }

    if (error.response?.status === 429) {
      toast.error(getRateLimitMessage(error), {
        id: "rate-limit",
        duration: 6000,
      });
    }

    return Promise.reject(error);
  },
);

export default axiosClient;
